import { useMemo, useState } from 'react';
import { Award, Download, Plus, Search } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Input,
  Select,
  useToast,
} from '@solara/ui-shared';
import type { AccreditationReportDto } from '@solara/api-client';
import { useReports, useGenerateReport } from '@solara/api-client/react';
import { useAuth } from '../auth/AuthContext';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

type CategoryFilter = 'all' | string;

const CATEGORIES: string[] = [
  'fta-audit',
  'iso-27001',
  'iso-22301',
  'peppol',
  'retention',
  'incident',
];

export default function ReportsPage() {
  const { t } = useTranslation('admin');
  const { locale } = useI18n();
  const { can } = useAuth();
  const { toast } = useToast();
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });

  const reportsQuery = useReports();
  const generateReport = useGenerateReport();
  const reports: AccreditationReportDto[] = reportsQuery.data ?? [];

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [draftCategory, setDraftCategory] = useState<string>('fta-audit');
  const [draftPeriod, setDraftPeriod] = useState<string>('2026-Q2');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return reports.filter((r) => {
      if (category !== 'all' && r.category !== category) return false;
      if (!q) return true;
      return r.title.toLowerCase().includes(q) || r.period.toLowerCase().includes(q);
    });
  }, [reports, query, category]);

  function humanSize(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
  }

  function download(r: AccreditationReportDto) {
    toast({
      title: t('reports.downloadToast', { title: r.title }),
      description: t('reports.downloadToastBody'),
      tone: 'info',
    });
  }

  async function generate() {
    try {
      await generateReport.mutateAsync({ category: draftCategory, period: draftPeriod });
      toast({
        title: t('reports.generateDialog.queued'),
        description: t('reports.generateDialog.queuedBody'),
        tone: 'success',
      });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Generate failed', tone: 'danger' });
    }
    setCreateOpen(false);
  }

  if (reportsQuery.isLoading) return <LoadingBlock />;
  if (reportsQuery.error) return <ErrorBanner error={reportsQuery.error} />;

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('reports.title')}</h1>
          <p className="text-fg-muted">{t('reports.subtitle')}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} disabled={!can('reports.generate')} className="gap-2">
          <Plus className="h-4 w-4" />
          {t('reports.newReport')}
        </Button>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 sm:flex-initial">
          <Search className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
          <input
            type="search"
            placeholder={t('reports.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full sm:w-72 h-10 ps-9 pe-3 rounded-md bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          />
        </div>
        <Select
          value={category}
          onChange={(e) => setCategory(e.target.value as CategoryFilter)}
          options={[
            { value: 'all', label: t('reports.filterCategory') },
            ...CATEGORIES.map((c) => ({ value: c, label: t(`reports.category.${c}`) })),
          ]}
          containerClassName="w-44"
        />
      </div>

      <Card>
        <CardBody>
          {filtered.length === 0 ? (
            <EmptyState icon={<Award className="h-6 w-6" />} title="No reports match." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fg-subtle">
                    <th className="text-start font-medium pb-2">{t('reports.table.title')}</th>
                    <th className="text-start font-medium pb-2">{t('reports.table.period')}</th>
                    <th className="text-start font-medium pb-2">{t('reports.table.category')}</th>
                    <th className="text-start font-medium pb-2">{t('reports.table.format')}</th>
                    <th className="text-end font-medium pb-2">{t('reports.table.size')}</th>
                    <th className="text-start font-medium pb-2">{t('reports.table.generatedAt')}</th>
                    <th className="text-start font-medium pb-2">{t('reports.table.by')}</th>
                    <th className="text-start font-medium pb-2">{t('reports.table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2.5">{r.title}</td>
                      <td className="py-2.5 text-xs num">{r.period}</td>
                      <td className="py-2.5">
                        <Badge tone="brand">{t(`reports.category.${r.category}`)}</Badge>
                      </td>
                      <td className="py-2.5 text-xs uppercase">{r.format}</td>
                      <td className="py-2.5 text-end num">{humanSize(r.sizeBytes)}</td>
                      <td className="py-2.5 text-xs num">{dateFmt.format(new Date(r.generatedAt))}</td>
                      <td className="py-2.5 text-xs" dir="ltr">{r.generatedBy}</td>
                      <td className="py-2.5">
                        <Button size="sm" variant="ghost" onClick={() => download(r)} className="gap-1">
                          <Download className="h-3.5 w-3.5" />
                          {t('reports.table.download')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Dialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={t('reports.generateDialog.title')}
        description={t('reports.generateDialog.subtitle')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={generate}>{t('reports.generateDialog.generate')}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Select
            label={t('reports.generateDialog.categoryLabel')}
            value={draftCategory}
            onChange={(e) => setDraftCategory(e.target.value)}
            options={CATEGORIES.map((c) => ({ value: c, label: t(`reports.category.${c}`) }))}
          />
          <Input
            label={t('reports.generateDialog.periodLabel')}
            value={draftPeriod}
            onChange={(e) => setDraftPeriod(e.target.value)}
            placeholder="2026-Q2"
            dir="ltr"
          />
        </div>
      </Dialog>
    </div>
  );
}
